<script setup>
// core
import {computed, onMounted, ref, getCurrentInstance} from "vue";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";

// components
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import CallNumberView from "@/components/Telephony/CallNumberView.vue";

// validate
import {validateSipName, validateSipPassword} from "@/helpers/dialogValidation";

const {t: $t} = useI18n()
const {proxy} = getCurrentInstance()
const store = useStore()
const isLoading = ref(false)
const fields = ref({
    name: '',
    password: ''
})
const initialFields = ref({
    name: '',
    password: ''
})

const currentExtension = computed(() => store.state.dialogParams?.currentExtension)
const number = computed(() => store.state.dialogParams?.info?.number)

const errors = computed(() => ({
    name: validateSipName(fields.value.name, $t),
    password: validateSipPassword(fields.value.password, $t),
}))

const isSubmittable = computed(() => {
    const filled = !!fields.value.name.trim() && !!fields.value.password
    const valid = !errors.value.name && !errors.value.password
    const changed =
        fields.value.name !== initialFields.value.name ||
        fields.value.password !== initialFields.value.password

    return filled && valid && changed
})

const onSubmit = async () => {
    if (!isSubmittable.value) return

    isLoading.value = true
    try {
        await store.dispatch('telephony/editExtension', {
            username: currentExtension.value.credentials.username,
            name: fields.value.name,
            password: fields.value.password,
            moshaToast: proxy.$moshaToast,
        })

        if (!!store.getters.getNextDialog) {
            store.commit("setDialog", store.getters.getNextDialog);
            store.commit("setNextDialog", "");
        } else {
            store.commit("closeDialog");
        }
    } finally {
        isLoading.value = false
    }
}

onMounted(() => {
    fields.value.name = currentExtension.value.name
    fields.value.password = currentExtension.value.credentials.password
    initialFields.value = {...fields.value}
})
</script>

<template>
    <slot name="header_close"/>

    <div class="wrapper_dialog">
        <h3 class="title">
            {{ $t('telephony.sip.edit.title') }}
            <div class="number-block">
                <CallNumberView :number="number"/>
            </div>
        </h3>

        <form class="content" @submit.prevent="onSubmit">
            <div class="field">
                <p class="subtitle">{{ $t('telephony.sip.edit.name') }}</p>
                <input v-model="fields.name" :class="{ input_error: !!errors.name }"/>
                <div class="input-errors" :class="{ 'is-shown': !!errors.name }">{{ errors.name }}</div>
            </div>

            <div class="field">
                <p class="subtitle">{{ $t('telephony.sip.edit.password') }}</p>
                <input v-model="fields.password" :class="{ input_error: !!errors.password }"/>
                <div class="input-errors" :class="{ 'is-shown': !!errors.password }">{{ errors.password }}</div>
            </div>

            <div class="action_container">
                <ButtonLoading type="submit" :loading="isLoading" :disabled="isLoading || !isSubmittable">
                    {{ $t('common.save') }}
                </ButtonLoading>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
@use "@/styles/dialog-form.scss";
</style>
